
<script>
import {computed, defineComponent, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {useI18n} from "vue-i18n";
import {useDonationSessionStore} from "../../stores/donationSession";
import LoadingSpinner from "../components/loadingSpinner.vue";
import {useAnalyticEventStore} from "../../stores/analyticEvents";
export default defineComponent({
  /**
   * Important note:
   * For local testing localhost is being blocked by the Helcim service and will not render the payment modal
   * use https://lvh.me:3000 instead to allow the modal to be rendered
   */
  name: 'HelcimPaymentButton',
  components: {LoadingSpinner},
  // components: {PaymentFormError},
  props: {
    redirect: {
      type: Boolean,
      default: true
    },
    enabled: {
      type: Boolean,
      default: true
    },
    userSignedIn: {
      type: Boolean,
      default: false
    },
    userData: {
      type: Object,
      default: null
    }
  },
  emits: ['payment-success', 'payment-error', 'click'],

  setup(props, { emit }) {
    // Router
    const route = useRoute();
    const router = useRouter();
    const {t} = useI18n();

    const organizationId = computed(() => route.params.id);
    const donationSessionStore = useDonationSessionStore();
    const analyticEventStore = useAnalyticEventStore();

    return {
      t,
      router,
      organizationId,
      donationSessionStore,
      analyticEventStore
    };
  },
  data() {
    return {
      loading: false,
      submittingDonation: false
    };
  },

  computed: {
     checkoutToken() {
       return this.donationSessionStore.donationSession.checkout_token;
     }
  },

  mounted() {
    window.addEventListener('message', this.handleMessage);
  },

  beforeUnmount() {
    window.removeEventListener('message', this.handleMessage);
  },

  methods: {
    async initPaymentForm() {
      this.loading = true;
      if(this.organizationId) {
        this.analyticEventStore.setOrganizationId(this.organizationId);
      } // had to add this in for the giving page

      const paymentFormOptions = this.userSignedIn && this.userData ? {
        customer_email: this.userData.email,
        customer_firstName: this.userData.firstName,
        customer_lastName: this.userData.lastName,
      } : null;

      //This will pass userSigned value to donationSession.js
      await this.donationSessionStore.loadPaymentForm(paymentFormOptions, this.userSignedIn).then(() => {
        if(this.checkoutToken) {
          appendHelcimPayIframe(this.checkoutToken);
          this.analyticEventStore.logEvent({
            action: `donation-intent-user-conversion`,
            user_id: this.donationSessionStore.currentDonorUserId,
            metadata: {
              user_id: this.donationSessionStore.currentDonorUserId
            }
          });
        } else {
          this.loading = false;
          this.$emit('payment-error');
        }
      }).catch((e) => {
        this.loading = false;
        removeHelcimPayIframe();
        this.$emit('payment-error', e?.message);
      });
    },

    async handleMessage(event) {
      const helcimPayJsIdentifierKey = 'helcim-pay-js-' + this.checkoutToken;
      if (event.data.eventName === helcimPayJsIdentifierKey) {
        // Payment was aborted - allow retry
        if (event.data.eventStatus === 'ABORTED') {
          this.loading = false;
          removeHelcimPayIframe();
          // pass this status off to update this donation to ERROR/FAILED
          this.donationSessionStore.updateFailedDonations(event.data.eventStatus);
          this.$emit('payment-error');
          this.analyticEventStore.logEvent({
            action: `helcim-payment-form-error`,
            user_id: this.donationSessionStore.currentDonorUserId,
            metadata: {
              data: event.data
            }
          });
        }
        if (event.data.eventStatus === 'HIDE') {
          this.loading = false; // payment modal was closed, allow retry
          removeHelcimPayIframe();
          // pass this status off to update this donation to ABANDONED
          this.donationSessionStore.updateFailedDonations(event.data.eventStatus);
          this.analyticEventStore.logEvent({
            action: `helcim-payment-form-closed`,
            user_id: this.donationSessionStore.currentDonorUserId,
            metadata: {
              data: event.data
            }
          });
        }

        if (event.data.eventStatus === 'SUCCESS' && !this.submittingDonation) {
          this.loading = true;
          this.donationSessionStore.confirmHelcimDonation(event.data);
          this.success = true;
          removeHelcimPayIframe();
          this.analyticEventStore.logEvent({
            action: `helcim-payment-form-success`,
            user_id: this.donationSessionStore.currentDonorUserId,
            metadata: {
              data: event.data
            }
          });
          if(this.redirect) {
            if(this.donationSessionStore.donationSession.redirectUrl){
              setTimeout(() => {
                const userSignedIn = this.donationSessionStore.donationSession.userAuthenticated;
                if (userSignedIn) {
                  const org = this.$route.params.org || this.donationSessionStore.organizationId;
                  window.location.href = `/${org}/donor/dashboard/overview?donated=true`;
                } else {
                  window.location.href = this.donationSessionStore.donationSession.redirectUrl;
                }
              }, 4000);
            } else {
              setTimeout(() => {
                this.router.push({path: '/donate/thank_you'});
              }, 4000);
            }
          }else {
            this.$emit('payment-success');
          }
        }
      }
    }
  }

});
</script>

<template>
  <button
    class="text-lg font-semibold"
    :class="{'loading': loading}"
    :disabled="!enabled || loading"
    @click="$emit('click')"
  >
    Give Now
  </button>
</template>
<style lang="scss" scoped>
// Buttons
button {
  @apply w-full py-2 px-4 rounded mt-2;
  @apply text-default_white active:animate-pulse;
  background-color: var(--button-color);
  color: var(--button-text);

  &:hover {
    background-color: var(--button-hover);
  }
  &:disabled {
    @apply opacity-30 cursor-not-allowed;
  }
  &.loading {
    @apply opacity-30 cursor-wait;
  }

}
</style>
